import type { NextRouter } from "next/router"
import type { ReactNode } from "react"
import React from "react"

import { Loading } from "@/lib/loading"
import { ReactPerformance } from "@/lib/react_performance"
import { updateQueryParams } from "@/lib/routing/updateQueryParams"
import type { Services } from "@/lib/services"
import { defaultServiceOption, serviceLabels } from "@/lib/services/constants"
import { ServicesContext } from "@/lib/services/context"
import { extractServiceFromQuery } from "@/lib/services/extractFromQuery"

type Props = {
  children: ReactNode
  loading: Loading.ContextData
  router: NextRouter
}

type State = {
  loadingConfig: Loading.Config
  update: boolean
}

export class ServicesContainerComponent extends React.Component<Props, State> {
  public state: State = {
    loadingConfig: {
      dependencies: [`routing`],
      processId: `services`,
    },
    update: false,
  }

  public componentDidMount = () => {
    this.componentMaybeReady()
  }

  public componentDidUpdate = async () => {
    await this.componentMaybeReady()
  }

  private componentMaybeReady = Loading.curryOnMaybeReady(this, {
    onDependenciesResolved: () => {},
  })

  public render = (): ReactNode => {
    const selected = this.getSelected(this.props)

    return (
      <ServicesContext.Provider
        value={{
          selectService: this.selectService,
          selected,
          selectedLabel: serviceLabels[selected],
        }}
      >
        {this.props.children}
      </ServicesContext.Provider>
    )
  }

  private selectService: Services.Functions.Select = async ({ service }) => {
    if (service === this.getSelected(this.props)) {
      return
    }

    await updateQueryParams({
      params: { specialty: service },
      router: this.props.router,
    })
  }

  private getSelected = (props: Props) => {
    return extractServiceFromQuery(props.router.query) || defaultServiceOption
  }

  public shouldComponentUpdate: ReactPerformance.Functions.ShouldComponentUpdate<
    Props,
    State
  > = ReactPerformance.curryShouldUpdateWithManagedState(
    this,
    ({ nextProps, nextState }) => {
      if (Loading.shouldComponentReady(nextProps, nextState)) {
        return { update: true }
      }

      if (this.getSelected(this.props) !== this.getSelected(nextProps)) {
        return { update: true }
      }

      if (this.props.children !== nextProps.children) {
        return { update: true }
      }
    }
  )

  private updateState = ReactPerformance.curryUpdateManagedState(this)
}
